import { Inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { WINDOW } from '../window.service';
import { Notification } from './notification';
import { NotificationType } from './notification-type';
import { ADPResourceService } from 'digital-core';

const SHOW_NOTIFICATION_EVENT = 'notification.show-notification';

@Injectable({
  providedIn: 'root'
})
export class SnackbarService {

  public constructor(
    private snackbar: MatSnackBar,
    private resourceService: ADPResourceService,
    @Inject(WINDOW) private window: Window,
  ) {
    window.addEventListener(SHOW_NOTIFICATION_EVENT, (e: CustomEvent<Notification>) => {
      if (e.detail.preferredType === NotificationType.Snackbar) {
        this.snackbar.open(e.detail.message);
      }
    });
  }

  /**
   * This will show a simple snackbar with just a message that will
   * automatically close after some time.
   *
   * @param message The message to display.
   *
   * @param duration The duration in milliseconds until the snackbar
   * automatically closes. If this is not specified, then the global default
   * value for snackbars will be used.
   *
   * @returns A reference to the opened up snackbar.
   */
  public showSimpleErrorMessage(message: string, duration?: number) {
    const config = this.getSimpleMessageConfig(duration, 'failureSnack');
    return this.snackbar.open(message, undefined, config);
  }

  /**
   * This will show a simple snackbar with just a message that will
   * automatically close after some time.
   *
   * @param message The message to display.
   *
   * @param duration The duration in milliseconds until the snackbar
   * automatically closes. If this is not specified, then the global default
   * value for snackbars will be used.
   *
   * @returns A reference to the opened up snackbar.
   */
  public showSimpleMessage(message: string, duration?: number) {
    const config = this.getSimpleMessageConfig(duration);
    return this.snackbar.open(message, undefined, config);
  }

  /**
   * This will show a simple snackbar with a message and action button that will
   * close on click onAction or automatically close after some time.
   *
   * @param message The message to display.
   *
   * @param action The action button text to display.
   *
   * @param duration The duration in milliseconds until the snackbar
   * automatically closes. If this is not specified, then the global default
   * value for snackbars will be used.
   *
   * @returns A reference to the opened up snackbar.
   */
  public showSimpleMessageWithAction(message: string, action: string, duration?: number) {
    const config = this.getSimpleMessageConfig(duration);
    return this.snackbar.open(message, action, config);
  }

  private getSimpleMessageConfig(duration?: number, panelClass: 'successSnack' | 'failureSnack' = 'successSnack'): MatSnackBarConfig {
    let config: MatSnackBarConfig;
    if (duration == null) {
      duration = 10;
    }

    let val = this.resourceService.getPortalSetting('Global_Snackbar_Duration_In_Milliseconds').Value;
    if (!val) {
      val = '1000';
    }
    config = {
      duration: duration * Number(val),
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: panelClass
    };

    return config;
  }
}
